/**
 * Form inputs shared by the lead editor: catalog-backed autocompletes with
 * inline creation ("+" behaviour of the legacy app) and the status combo.
 */

import {
  AutocompleteArrayInput,
  AutocompleteInput,
  useCreate,
  useGetList,
} from 'react-admin';
import { CANONICAL_STATUSES } from './LeadFilterInputs';

interface LookupChoice {
  id: number;
  name: string;
}

const listParams = {
  pagination: { page: 1, perPage: 1000 },
  sort: { field: 'name', order: 'ASC' as const },
};

/**
 * Single-value catalog selector; typing a new value creates it on the fly.
 */
export const LookupAutocompleteInput = ({
  source,
  resource,
  label,
}: {
  source: string;
  resource: string;
  label: string;
}) => {
  const { data } = useGetList<LookupChoice>(resource, listParams);
  const [create] = useCreate<LookupChoice>();

  const handleCreate = async (name?: string) => {
    if (!name?.trim()) return undefined;
    return await create(
      resource,
      { data: { name: name.trim() } },
      { returnPromise: true }
    );
  };

  return (
    <AutocompleteInput
      source={source}
      label={label}
      choices={data ?? []}
      optionText="name"
      optionValue="id"
      onCreate={handleCreate}
      fullWidth
      helperText={false}
    />
  );
};

/**
 * Multi-value catalog selector (tags, interests) with inline creation.
 */
export const LookupAutocompleteArrayInput = ({
  source,
  resource,
  label,
}: {
  source: string;
  resource: string;
  label: string;
}) => {
  const { data } = useGetList<LookupChoice>(resource, listParams);
  const [create] = useCreate<LookupChoice>();

  const handleCreate = async (name?: string) => {
    if (!name?.trim()) return undefined;
    return await create(
      resource,
      { data: { name: name.trim() } },
      { returnPromise: true }
    );
  };

  return (
    <AutocompleteArrayInput
      source={source}
      label={label}
      choices={data ?? []}
      optionText="name"
      optionValue="id"
      onCreate={handleCreate}
      fullWidth
      helperText={false}
    />
  );
};

/**
 * Status combo: canonical pipeline values plus free text (legacy allowed
 * arbitrary statuses).
 */
export const StatusInput = ({ source, label }: { source: string; label: string }) => (
  <AutocompleteInput
    source={source}
    label={label}
    choices={CANONICAL_STATUSES.map((s) => ({ id: s, name: s }))}
    onCreate={(value?: string) =>
      value?.trim() ? { id: value.trim(), name: value.trim() } : undefined
    }
    fullWidth
    helperText={false}
  />
);
